import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import styled from "styled-components";
import { LoginPage } from "../Auth";
import { oauthAuthorize } from "../../api/oauth";

export default function OAuthAuthorizePage() {
  const [_params] = useSearchParams();

  const params = useMemo(
    () => ({
      clientId: _params.get("client_id") || "",
      redirectUri: _params.get("redirect_uri") || "",
      responseType: _params.get("response_type") || "",
      state: _params.get("state") || "",
      scope: _params.get("scope") || "",
    }),
    []
  );

  const [accessToken, setAccessToken] = useState<string | null>(
    () => localStorage.getItem("ui_temp_access_token") || null
  );

  return !params.clientId || !params.redirectUri ? (
    <Error>
      {!params.clientId ? "client_id is required" : "redirect_uri is required"}
    </Error>
  ) : !accessToken ? (
    <LoginPage
      onSuccess={(token) => {
        localStorage.setItem("ui_temp_access_token", token);
        setAccessToken(token);
      }}
    />
  ) : (
    <ConsentPage
      accessToken={accessToken}
      clientId={params.clientId}
      redirectUri={params.redirectUri}
      responseType={params.responseType}
      state={params.state}
      scope={params.scope}
      logout={() => {
        localStorage.removeItem("ui_temp_access_token");
        setAccessToken(null);
      }}
    />
  );
}

const ConsentContainer = styled.div`
  max-width: 500px;
  width: 100%;
  padding: 32px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  margin: 100px auto;
  position: relative;
  & > h1 {
    margin-bottom: 10px;
    font-size: 24px;
    font-weight: 600;
  }
  & > p {
    font-weight: 400;
    margin-bottom: 18px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
  }
  & > p > button {
    background: none;
    color: #00bcd4;
    cursor: pointer;
  }
`;
const Scopes = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  & > li {
    margin-bottom: 8px;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.8);
  }
  & > li::before {
    content: "•";
    color: #00bcd4;
    margin-right: 8px;
  }
  padding: 0 20px;
  width: 100%;
  margin-top: 20px;
  margin-bottom: 40px;
`;
const Button = styled.button`
  background-color: #00bcd4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  &:not(:disabled):hover {
    background-color: #0097a7;
  }
  &:disabled {
    background-color: rgba(0, 188, 212, 0.5);
    cursor: not-allowed;
  }
`;
const ScopeMap: any = {
  identify: "View your username, avatar",
  email: "Access your email address",
  profile: "View your profile information",
};
function ConsentPage({
  accessToken,
  clientId,
  redirectUri,
  responseType,
  state,
  scope,
  logout,
}: {
  accessToken: string;
  clientId: string;
  redirectUri: string;
  responseType: string;
  state: string;
  scope: string;
  logout: () => void;
}) {
  const [loading, setLoading] = useState(false);
  return (
    <ConsentContainer>
      <h1>Authorize [Application Name]?</h1>
      <p>
        You're signed in as [Username].{" "}
        <button onClick={logout}>Logout?</button>
      </p>
      <p>By authorizing, you will allow [Application Name] to:</p>
      <Scopes>
        {scope.split(" ").map((s, i) => (
          <li key={i}>{!ScopeMap[s] ? s : ScopeMap[s]}</li>
        ))}
      </Scopes>
      <p>
        [Application Name] is a third party application unassociated with HELIX
        or Hypersonic Laboratories.
      </p>
      <Button
        disabled={loading}
        onClick={async () => {
          setLoading(true);

          const result = await oauthAuthorize(accessToken, {
            clientId,
            responseType,
            scope,
            redirectUri,
          });

          if (!result.success) {
            setLoading(false);
            return alert(result.error);
          }

          const url = new URL(redirectUri);
          url.searchParams.set("code", result.data.code);
          if (state) url.searchParams.set("state", state);

          window.location.href = url.toString();
        }}
      >
        Allow
      </Button>
    </ConsentContainer>
  );
}

const ErrorContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 1rem;
`;
function Error({ children = null }: { children?: React.ReactNode }) {
  return <ErrorContainer>{children}</ErrorContainer>;
}
